'use client'

/*
 * `shadcn-nextjs-craft-landing-page-2.0.0` (бүрэн лицензтэй) загварын
 * `src/components/blocks/portfolio/portfolio.tsx` — ЭХ КОД ЯГ ХЭВЭЭР,
 * загварынхаа зураг, mock өгөгдөлтэйгээ.
 *
 * Ганц өөрчлөлт: доод талын товч эх дээрээ Radix-ийн `asChild`-ыг ашигладаг
 * бол манай `Button` нь base-ui тул `render` + `nativeButton={false}` болов
 * (эс бөгөөс огт компайл болохгүй).
 *
 * Хэсэг хэсгээр нь бодит брэндийн өгөгдөл рүү шилжүүлнэ.
 */

import { useRef, useState, useEffect } from 'react'

import { ArrowRightIcon } from 'lucide-react'

import Link from 'next/link'

import { useScroll, motion, useTransform, type MotionValue } from 'motion/react'

import { SECTION_TITLE } from '@/lib/typography'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { MotionPreset } from '@/components/ui/motion-preset'

import { cn } from '@/lib/utils'
import BlurText from '@/components/ui/blur-text'
import { useDictionary } from '@/lib/i18n/provider'
import { resolveImageSrc } from '@/lib/image-src'
import type { BrandSummary } from '@/types/website'

// Custom hook to track window size
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: 1280,
    height: 800
  })

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    handleResize() // Call once initially to set the size

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowSize
}

/*
 * Дэлгэц бүрийн нислэгийн утга.
 *
 * ⚠️ Эх код нь ЯГ 4 картын утгыг (`div1…div4`) гараар бичсэн байсныг мөр,
 * баганаар нь ерөнхийлөв — утгууд нь ХЭВЭЭР:
 *   • `x` — 1, 3, 5-р карт (зүүн багана) баруун талаас нисч ирнэ
 *   • `xAlt` — 2, 4, 6-р карт (баруун багана) бараг шулуухан
 *   • `y` — эхний мөрийн өндөр, `yStep` — мөр бүрийн нэмэгдэл
 * Ингэснээр картын тоо 4-өөс өөр байсан ч (одоо 6) ижил зан төлөвтэй.
 */
function getAnimationValues(width: number) {
  if (width >= 1280) {
    return { x: '520px', xAlt: '-30px', y: 300, yStep: 190, ...TEXT_VALUES }
  } else if (width >= 1024) {
    return { x: '500px', xAlt: '-10px', y: 280, yStep: 190, ...TEXT_VALUES }
  } else if (width >= 945) {
    return { x: '470px', xAlt: '-10px', y: 270, yStep: 160, ...TEXT_VALUES }
  } else if (width >= 884) {
    return { x: '430px', xAlt: '-10px', y: 280, yStep: 170, ...TEXT_VALUES }
  } else if (width >= 768) {
    return { x: '370px', xAlt: '-20px', y: 270, yStep: 150, ...TEXT_VALUES }
  } else {
    // Mobile - no animation
    return {
      x: '0px',
      xAlt: '0px',
      y: 0,
      yStep: 0,
      textTranslateY: '0px',
      textOpacity: 1,
      textScale: 1,
      divScale: 1
    }
  }
}

/** Нэр, тайлбарын мөрийн гарц — эх дээрээ дэлгэц бүрд ижил */
const TEXT_VALUES = {
  textTranslateY: '-180px',
  textOpacity: 0,
  textScale: 0.75,
  divScale: 0.75
}

/** Эх блокийн эргэлтүүд — эхнийх шулуун, дараах нь ээлжлэн */
const TILTS = [0, 12, -2, 8]

const Portfolio = ({ brands }: { brands: BrandSummary[] }) => {
  const t = useDictionary()

  /*
   * Эх блок нь ЯГ 4 картын мэдээллийг кодондоо бичсэн бөгөөд хөдөлгөөн нь
   * тэр 4 (`div1…div4`) дээр тулгуурладаг. Тиймээс бүтцийг нь хэвээр
   * үлдээж, зөвхөн АГУУЛГЫГ нь CMS-ийн брэндээр сольсон.
   */
  const containerRef = useRef<HTMLDivElement>(null)
  const size = useWindowSize()
  const isMobile = size.width < 768

  // Get responsive animation values based on window width
  const animValues = getAnimationValues(size.width)

  // Determine scroll offset based on screen width
  const scrollOffset: ['start end' | 'center end', 'center start'] =
    size.width < 2000 ? ['start end', 'center start'] : ['center end', 'center start']

  // Track scroll progress of the container
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: scrollOffset
  })

  const cards = brands.map(brand => ({
    ...brand,

    /* Лого нь үндсэн зураг; байхгүй бол нүүр зураг руу ухарна */
    image: resolveImageSrc(brand.logo_url) ?? resolveImageSrc(brand.cover_url) ?? ''
  }))

  /* Дээшээ нисэх карт дээд блокийг дардаг тул хэсгийнхээ ирмэгээр таслав */
  return (
    <section id='portfolio' className='relative flex-1 overflow-hidden'>
      <MotionPreset
        fade
        blur
        transition={{ duration: 0.5 }}
        delay={0.15}
        className='mx-auto flex w-full max-w-6xl flex-col min-[1147px]:border-x'
      >
        {/* Header */}
        <div className='space-y-2.5 px-4 py-16 md:px-6 lg:px-8'>
          <MotionPreset fade blur slide={{ direction: 'down', offset: 50 }} transition={{ duration: 0.5 }}>
            <Badge variant='outline' className='h-auto rounded-full px-1.5'>
              Portfolio
            </Badge>
          </MotionPreset>

          {/* Эх дээрээ тайлбар нь баруун талдаа зэрэгцдэг; энд гарчгийн ДООР */}
          <div className='flex max-w-2xl flex-col gap-4'>
            <MotionPreset delay={0.3} transition={{ duration: 0.5 }}>
              <h2 className={SECTION_TITLE}>
                <BlurText text={t.brands.featuredHeading} delay={50} animateBy='words' direction='bottom' />
              </h2>
            </MotionPreset>

            <MotionPreset
              delay={0.3}
              fade
              blur
              slide={{ direction: 'down', offset: 50 }}
              transition={{ duration: 0.5 }}
            >
              <p className='text-muted-foreground max-w-xl text-justify text-xl hyphens-auto'>
                Дэлхийн тэргүүлэх үйлдвэрлэгчидтэй хамтран Монголын зах зээлд түгээж буй брэндүүдээс онцлон сонгов. Карт
                дээр дарж танилцуулга, бүтээгдэхүүний жагсаалттай нь танилцана уу.
              </p>
            </MotionPreset>
          </div>
        </div>

        <MotionPreset delay={0.75} fade blur transition={{ duration: 0.5 }}>
          <Separator />
        </MotionPreset>

        <div
          ref={containerRef}
          className='relative grid gap-x-12.5 gap-y-16 px-4 py-16 max-sm:gap-y-8 sm:grid-cols-2 md:px-6 lg:px-8'
        >
          {cards.map((brand, index) => (
            <PortfolioCard
              key={brand.slug}
              brand={brand}
              index={index}
              total={cards.length}
              values={animValues}
              isMobile={isMobile}
              progress={scrollYProgress}
            />
          ))}
        </div>

        <MotionPreset delay={0.3} fade blur transition={{ duration: 0.6 }}>
          <Separator />
        </MotionPreset>

        {/* Action Button */}
        <div className='my-11'>
          <MotionPreset
            className='flex items-center justify-center gap-4'
            fade
            blur
            slide={{ direction: 'down', offset: 50 }}
            delay={0.35}
            transition={{ duration: 0.6 }}
          >
            <Button
              variant='outline'
              className='gap-2 rounded-full px-3'
              render={<Link href='#brands-all' />}
              nativeButton={false}
            >
              {t.common.allBrands} <ArrowRightIcon className='size-4' />
            </Button>
          </MotionPreset>
        </div>
      </MotionPreset>
    </section>
  )
}

/**
 * Нэг карт — эх блокийн `div1…div4`-ийн ЯГ тэр хөдөлгөөн, зөвхөн индексээс
 * тооцоологдож байгаагаараа ялгаатай.
 */
const PortfolioCard = ({
  brand,
  index,
  total,
  values,
  isMobile,
  progress
}: {
  brand: BrandSummary & { image: string }
  index: number
  total: number
  values: ReturnType<typeof getAnimationValues>
  isMobile: boolean
  progress: MotionValue<number>
}) => {
  const column = index % 2
  const row = Math.floor(index / 2)

  const offsetX = column === 0 ? values.x : values.xAlt
  const offsetY = values.y === 0 ? '0px' : `-${values.y + row * values.yStep}px`
  const tilt = isMobile ? 0 : TILTS[index % TILTS.length]

  const translateX = useTransform(progress, [0, 0.5], [offsetX, '0px'])
  const translateY = useTransform(progress, [0, 0.5], [offsetY, '0px'])
  const rotate = useTransform(progress, [0, 0.5], [tilt, 0])
  const scale = useTransform(progress, [0, 0.5], [values.divScale, 1])

  const textOpacity = useTransform(progress, [0, 0.5], [values.textOpacity, 1])
  const textTranslateY = useTransform(progress, [0, 0.5], [values.textTranslateY, '0px'])
  const textScale = useTransform(progress, [0, 0.5], [values.textScale, 1])

  return (
    <motion.div
      style={{ translateY, translateX, scale, rotate, zIndex: total - index }}
      transition={{ ease: 'easeInOut', duration: 0.6 }}
      className={cn('group relative', !isMobile && 'will-change-scroll perspective-distant transform-3d')}
    >
      <Link href={`/brands/${brand.slug}`} className={cn('flex flex-col gap-6 **:transition-all **:duration-300')}>
        <div>
          <div
            className={cn(
              'overflow-hidden rounded-[12px]',
              index % 2 === 0 ? 'group-hover:rotate-3' : 'group-hover:-rotate-3'
            )}
          >
            {/* Брэндийн зураг өөрөө дэвсгэртэйгээ ирдэг тул хүрээ, дотор зайгүй */}
            <img src={brand.image} alt={brand.name} className='w-full rounded-[11px]' />
          </div>
        </div>

        <motion.div
          style={{ opacity: textOpacity, translateY: textTranslateY, scale: textScale }}
          transition={{ type: 'spring', stiffness: 100, damping: 30, mass: 1 }}
          className='flex items-center justify-between gap-4 md:gap-6'
        >
          <div className='flex min-w-0 flex-col gap-2.5'>
            <span className='truncate text-2xl font-semibold'>{brand.name}</span>
            {brand.tagline && <span className='text-muted-foreground truncate text-xl'>{brand.tagline}</span>}
          </div>

          <Button
            variant='outline'
            size='icon'
            nativeButton={false}
            render={<span />}
            className='group-hover:bg-primary! group-hover:border-primary group-hover:text-primary-foreground! shrink-0 rounded-full bg-clip-border px-4!'
          >
            <ArrowRightIcon className='transition-transform duration-500 group-hover:-rotate-45' />
          </Button>
        </motion.div>
      </Link>
    </motion.div>
  )
}

export default Portfolio
